import { IkeToken } from '@ikegps/core-api';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { MultiListSearch } from '../multi.list.search/multi.list.search';
import { MultiChooserState } from './multi.list.chooser.state';
import { CSS, Style } from './multi.list.chooser.style';
import { NestedListChooserState } from './nested.list.chooser.state';
import { SelectListChooserState } from './select.list.chooser.state';

addStyles(Style);

function focusNode(e: MithrilVNode) {
	setTimeout(() => e.dom.focus(), 10);
}

export const MultiListChooserContent = {
	view(state: MultiChooserState) {
		const groupNodes: MithrilVNode[] = [];
		const filteredItemNodes: MithrilVNode[] = [];
		const groups = state.groups;

		if (state.hasError()) {
			return m('div', { className: CSS.container }, Translate(state.error));
		}

		if (groups == null) {
			return m('div', { className: CSS.container }, Spinner.view());
		}

		if (Services.MultiListSearch.inputfilterState.hasFilter()) {
			const flattenedEntryList = state.getAllOptions();

			const optionsValueKeyMap = new Map<string, string[]>(
				flattenedEntryList.map((x) => [x.map((y) => y.value).join(' > '), x.map((z) => z.key)])
			);

			const filteredEntryList = flattenedEntryList
				.map((x) => x.map((y) => y.value).join(' > '))
				.filter((z) => z.toLowerCase().includes(Services.MultiListSearch.inputfilterState.filter.toLowerCase()));

			for (let i = 0; i < filteredEntryList.length; i++) {
				const filteredEntry = filteredEntryList[i];

				const id = `filtered-list-item-${i}`;
				const inputId = `${id}-input`;

				const isSelected = state.value && state.value.join(' > ') === filteredEntry;
				const shouldFocus = i === 0 && isSelected;

				const filteredItemNode = m('div', { className: CSS.group.selection, id }, [
					m('input', {
						type: 'radio',
						id: inputId,
						checked: isSelected ? 'checked' : null,
						onchange: () => state.setValue(optionsValueKeyMap.get(filteredEntry)),
						oncreate: () => (shouldFocus ? focusNode : null),
					}),
					m('label', { for: inputId }, filteredEntry),
				]);
				filteredItemNodes.push(filteredItemNode);
			}
			const groupNode = m('div', { className: CSS.group.container }, [
				m('label', { className: CSS.group.title, for: name }, Translate(T.SEARCH_RES)),
				m('div', { className: CSS.group.selectionWrap }, filteredItemNodes),
			]);
			groupNodes.push(groupNode);
		} else {
			let isColorField = false;

			if (
				// We have the correct token
				Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) &&
				// And the field is one of the two list types which can have colors
				(state instanceof SelectListChooserState || state instanceof NestedListChooserState) &&
				// And this instance is the one that is marked as the color field
				state.field.collection.schema.metadata.mapColorFieldId === state.field.schema.id
			) {
				isColorField = true;
			}

			for (let i = 0; i < groups.length; i++) {
				const group = groups[i];
				const name = `multi-list-group-${i}`;

				const itemNodes: MithrilVNode[] = [];
				for (let j = 0; j < group.items.length; j++) {
					const item = group.items[j];
					const id = `${name}-${j}`;
					const inputId = `${id}-input`;
					const isSelected = group.selected === item.key;
					const shouldFocus = i === 0 && isSelected;
					const hasColor = item.color != null;

					const stateValue = [...groups.slice(0, i).map((g) => g.selected), item.key];

					const children: MithrilVNode[] = [
						m('input', {
							type: 'radio',
							id: inputId,
							name,
							checked: isSelected ? 'checked' : null,
							onchange: () => state.setValue(stateValue),
							oncreate: shouldFocus ? focusNode : null,
						}),
						m('label', { for: inputId }, item.value),
					];

					if (isColorField && hasColor) {
						children.push(m('div', { className: CSS.colorIndicator, style: { 'background-color': item.color } }));
					}

					itemNodes.push(m('div', { className: CSS.group.selection, id }, children));
				}

				if (itemNodes.length > 0) {
					const groupNode = m('div', { className: CSS.group.container }, [
						m('label', { className: CSS.group.title, for: name }, group.title),
						m('div', { className: CSS.group.selectionWrap }, itemNodes),
					]);
					groupNodes.push(groupNode);
				}
			}
		}

		const chooserNode = m('div', { className: CSS.chooserContainer }, groupNodes);
		const filterNode = new MultiListSearch().nodes;
		const dialogNode = m('div', { className: CSS.container }, [filterNode, chooserNode]);

		return dialogNode;
	},
};

export const MultiListChooserTitle = {
	view(ctrl: MultiChooserState): string {
		return Translate(T.SELECT, ctrl.label);
	},
};
